import React, { ReactElement } from 'react';
import { Field, Fields, FieldArray } from 'redux-form/immutable';
import { Button, FormControl, Grid, InputAdornment, InputLabel, MenuItem } from '@material-ui/core';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import { legacyParse, convertTokens } from '@date-fns/upgrade/v2';

import config from 'config';
import DeedsApi from 'entities/deed/api';
import DonationCreditRuleType from 'entities/donationCreditRule/model';
import FormDateWithStartAndEnd from 'retired/FormDateWithStartAndEnd';
import FormIncludeExcludeSelector from 'retired/FormIncludeExcludeSelector';
import FormTextField from 'retired/FormTextField';
import FormEntitySelector from 'retired/FormEntitySelector';
import FormValueSelector from 'retired/FormValueSelector';
import * as validators from 'utils/validators';
import { currencies } from 'utils/currencyFormat';
import DialogButton from 'retired/DialogButton';
import FormLocationSelector from 'retired/FormLocationSelector';
import FormNonprofitsSelector from 'retired/FormNonprofitsSelector';
import FormSelect from 'retired/FormSelect';
import FormCheckbox from 'retired/FormCheckbox';
import { TotalLimitLabel } from 'containers/DonationRulesListPage/TotalLimitLabel';

const required = validators.required();

const CurrencyCodeSelector = ({ budget, ...rest }: { budget: any; rest: any }): ReactElement => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <FormSelect displayEmpty {...rest}>
    {[{ currencyCode: '', currency: { label: 'Select a currency' } }]
      .concat(
        Object.keys(currencies).map((currencyCode) => ({
          currencyCode,
          currency: currencies[currencyCode],
        }))
      )
      .map(({ currencyCode, currency }) => (
        <MenuItem
          key={currencyCode}
          value={currencyCode}
          disabled={budget?.currencyCode !== currencyCode}
        >
          {currency.label}
        </MenuItem>
      ))}
  </FormSelect>
);

// @NOTE-RT: See https://en.wikipedia.org/wiki/ISO_8601#Durations
const PeriodDurationSelector = (props): ReactElement => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <FormSelect displayEmpty {...props}>
    {[
      { value: '', label: '' },
      { value: 'Y', label: 'Years' },
      { value: 'M', label: 'Months' },
      { value: 'W', label: 'Weeks' },
    ].map(({ value, label }) => (
      <MenuItem key={value} value={value}>
        {label}
      </MenuItem>
    ))}
  </FormSelect>
);

const VolunteerTimeDonationCreditRule = ({
  donationCreditRule,
  locations,
  budget,
  categories,
  pristine,
  invalid,
  donationCreditRuleId,
  remove,
}: {
  donationCreditRule: DonationCreditRuleType;
  locations: any;
  budget: any;
  categories: any;
  pristine: boolean;
  invalid: boolean;
  remove: () => void;
  donationCreditRuleId: string;
}): ReactElement => {
  const selectedCurrencyCode =
    donationCreditRule?.get('currencyCode') || budget.currencyCode || 'USD';
  const selectedCurrency = currencies[selectedCurrencyCode];

  let spellOut = 'Credit amount is required';

  if (Number.isFinite(Number(donationCreditRule?.get('creditAmountPerHour')))) {
    spellOut = `Give ${selectedCurrency.symbol}${donationCreditRule.get(
      'creditAmountPerHour'
    )} of credit for every hour an employee volunteers`;

    if (donationCreditRule?.get('limitHoursPerUser')) {
      spellOut += `, until they reach ${donationCreditRule?.get('limitHoursPerUser')} (equal to ${
        selectedCurrency.symbol
      }${
        donationCreditRule.get('creditAmountPerHour') * donationCreditRule.get('limitHoursPerUser')
      } of credit)`;
    }

    const categoriesString = donationCreditRule
      ?.get('categories')
      .map((id) => `'${categories.get(id).get('name')}'`)
      .join(', ');
    if (donationCreditRule?.get('categoriesExclude')) {
      if (donationCreditRule?.get('categories').size) {
        spellOut += ` and excluding deeds and nonprofits in the categories ${categoriesString}`;
      }
    } else if (donationCreditRule?.get('categories').size) {
      spellOut += ` and including deeds and nonprofits in the categories ${categoriesString}`;
    }

    if (donationCreditRule?.get('nonprofitsExclude')) {
      if (donationCreditRule?.get('nonprofits').size) {
        spellOut += ' and excluding nonprofits selected above';
      }
    } else if (donationCreditRule?.get('nonprofits').size) {
      spellOut += ' and including nonprofits selected above';
    }

    if (donationCreditRule?.get('deedsExclude')) {
      if (donationCreditRule?.get('deeds').size) {
        spellOut += ' and excluding deeds selected above';
      }
    } else if (donationCreditRule?.get('deeds').size) {
      spellOut += ' and including deeds selected above';
    }

    if (donationCreditRule?.get('locationsExclude')) {
      if (donationCreditRule?.get('locations').size) {
        spellOut += ' and excluding employee locations selected above';
      }
    } else if (donationCreditRule?.get('locations').size) {
      spellOut += ' and only including employee locations selected above';
    }

    if (donationCreditRule?.get('limitTotal')) {
      spellOut += `. End the rule when we've issued ${
        selectedCurrency.symbol
      }${donationCreditRule.get('limitTotal')}`;
    }

    if (donationCreditRule?.get('endingAt')) {
      spellOut += ` or reached the date: ${format(
        legacyParse(donationCreditRule.get('endingAt')),
        convertTokens('YYYY-MM-DD')
      )}`;
    }

    spellOut += '.';
  }

  const validateCategories = (value, rule) => {
    if (rule && !rule.get('categoriesExclude') && !value?.size) {
      return 'The rule is set to only include deeds and nonprofits with certain categories but none are selected';
    }
    return undefined;
  };

  const validateNonprofits = (value: string, rule: DonationCreditRuleType) => {
    if (
      rule &&
      !rule.get('nonprofitsExclude') &&
      !rule.get('partneredNonprofits') &&
      !value?.size
    ) {
      return 'The rule is set to only include certain nonprofits and none are selected';
    }
    return undefined;
  };

  const validateDeeds = (value: string, rule: DonationCreditRuleType) => {
    if (rule && !rule.get('deedsExclude') && !value?.size) {
      return 'The rule is set to only include certain deeds and none are selected';
    }
    return undefined;
  };

  const validateLocations = (value: string, rule: DonationCreditRuleType) => {
    if (rule && !rule.get('locationsExclude') && !value?.size) {
      return 'The rule is set to only include certain locations and none are selected';
    }
    return undefined;
  };

  const validateCreditAmount = (value: string, rule: DonationCreditRuleType) => {
    if (value !== '') {
      if (Number(value) <= 0) {
        return 'Employee credit rate must be greater than or equal to 0';
      }

      if (Number.isFinite(rule.get('limitTotal')) && Number(value) >= rule.get('limitTotal')) {
        return 'Employee credit rate must be less than the spending limit';
      }

      return undefined;
    }

    return 'Employee credit rate is required';
  };

  const calculateMax = (rule: DonationCreditRuleType): number => {
    if (rule && Number.isFinite(rule.get('limitTotal')) && rule.get('creditAmountPerHour') > 0) {
      return rule.get('limitTotal') / rule.get('creditAmountPerHour');
    }
    return Infinity;
  };

  const max = calculateMax(donationCreditRule);

  const validateHours = (value: string) => {
    if (value !== '') {
      if (Number(value) <= 0) {
        return 'Employee hours cap must be greater than or equal to 0';
      }

      if (Number(value) > max) {
        return 'Max possible employee credit must be less than the spending limit';
      }
    }

    return undefined;
  };

  const validateTotalLimit = (value: string, rule: DonationCreditRuleType) => {
    if (value !== '') {
      if (Number(value) <= 0) {
        return 'Spending limit must be greater than or equal to 0';
      }

      if (rule.get('totalIssued') > Number(value)) {
        return 'You can not set the spending limit to less than what has already been spent';
      }

      if (Number.isFinite(budget?.limitTotal) && budget?.limitTotal < Number(value)) {
        return 'You can not set the spending limit to more than the parent budget';
      }
    }

    return undefined;
  };

  return (
    <div
      style={{
        border: '1px solid rgba(0, 0, 0, 0.2)',
        padding: 30,
        marginBottom: 30,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 30 }}>
        <div style={{ flexGrow: 1 }}>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>Rule Name</InputLabel>
          </FormControl>
          <FormControl style={{ width: '100%' }}>
            <Field
              name="title"
              component={FormTextField}
              placeholder="Rule Name"
              validate={required}
              required
              alwaysDisplayErrors
            />
          </FormControl>
        </div>
        {donationCreditRuleId !== 'new' && !donationCreditRule?.get('donationsCreditCount') && (
          <DialogButton
            style={{ display: 'inline-block' }}
            title="Remove Rule"
            text="Are you sure you want to remove this Donation Credit Rule?"
            confirmLabel="Remove"
            cancelLabel="Cancel"
            iconButton
            handleConfirm={remove}
          >
            <DeleteIcon />
          </DialogButton>
        )}
      </div>
      <div style={{ marginBottom: 10 }}>
        <Grid container spacing={3}>
          <Grid item md={4}>
            <Field
              name="currencyCode"
              component={CurrencyCodeSelector}
              validate={required}
              required
              type="string"
              budget={budget}
              defaultValue={selectedCurrencyCode}
              label="Rule Currency"
              labelProps={{
                shrink: true,
              }}
              formControlProps={{
                style: { width: '70%', height: 17 },
              }}
            />
          </Grid>
          <Grid item md={4}>
            <TotalLimitLabel />
            {selectedCurrency.symbol}
            <Field
              name="limitTotal"
              component={FormTextField}
              placeholder="Total limit"
              max={Number.isFinite(budget?.limitTotal) ? budget.limitTotal : Infinity}
              min={0}
              type="number"
              validate={validateTotalLimit}
              alwaysDisplayErrors
            />
          </Grid>
          <Grid item md={4}>
            <FormControl style={{ width: '100%', height: 17 }}>
              <InputLabel shrink>Start &amp; End Date (Optional)</InputLabel>
            </FormControl>
            <div>
              <Fields
                names={[`startingAt`, `endingAt`]}
                component={FormDateWithStartAndEnd}
                future
                displayTimeAndTimeZone
              />
            </div>
          </Grid>
        </Grid>
      </div>
      <div style={{ marginBottom: 30 }}>
        <Grid container spacing={3}>
          <Grid item md={4}>
            <FormControl style={{ width: '100%', height: 17 }}>
              <InputLabel shrink>Credit amount</InputLabel>
            </FormControl>
            {selectedCurrency.symbol}
            <Field
              name="creditAmountPerHour"
              component={FormTextField}
              placeholder="Credit amount"
              max={
                Number.isFinite(donationCreditRule?.get('limitTotal'))
                  ? donationCreditRule?.get('limitTotal')
                  : Infinity
              }
              min={0}
              required
              validate={validateCreditAmount}
              type="number"
              InputProps={{
                endAdornment: <InputAdornment position="end">/h</InputAdornment>,
              }}
              alwaysDisplayErrors
            />
          </Grid>
          <Grid item md={4}>
            <FormControl style={{ width: '100%', height: 17 }}>
              <InputLabel shrink>Max hours per employee</InputLabel>
            </FormControl>
            <Field
              name="limitHoursPerUser"
              component={FormTextField}
              placeholder="Hours limit"
              max={max}
              min={0}
              validate={validateHours}
              type="number"
              alwaysDisplayErrors
            />
          </Grid>
          <Grid item md={4}>
            <FormControl style={{ width: '100%', height: 17 }}>
              <InputLabel shrink>Credit validity period</InputLabel>
            </FormControl>
            <Field
              name="validityDuration.periods"
              component={FormTextField}
              label="# of"
              min={0}
              type="number"
              style={{
                width: '33%',
                marginRight: '5%',
              }}
              alwaysDisplayErrors
            />
            <Field
              name="validityDuration.period"
              component={PeriodDurationSelector}
              label="Duration in"
              defaultValue={donationCreditRule?.validityDurationPeriod}
              labelProps={{
                shrink: true,
              }}
              formControlProps={{
                style: { width: '60%', height: 17 },
              }}
            />
          </Grid>
        </Grid>
      </div>
      <div style={{ margin: '20px 0 0 0' }}>
        <Field
          name="bypassBudgetLimitPerUser"
          component={FormCheckbox}
          label="Do not apply individual budget cap to these funds"
        />
      </div>
      {!config.isProduction && (
        <div style={{ margin: '0 0 20px 0' }}>
          <Field
            name="appliesToGuests"
            component={FormCheckbox}
            label="Apply to guest volunteers"
          />
        </div>
      )}
      {categories.size > 0 ? (
        <div style={{ marginBottom: 30 }}>
          <FormIncludeExcludeSelector
            name="categoriesExclude"
            labelText="the following categories:"
          />
          <FieldArray
            name="categories"
            component={FormValueSelector}
            selectMultiple
            options={categories}
            validate={validateCategories}
          />
        </div>
      ) : null}
      <div style={{ marginBottom: 30 }}>
        <FormIncludeExcludeSelector
          name="nonprofitsExclude"
          labelText="the following nonprofits:"
        />
        <div style={{ marginTop: 10 }}>
          <FieldArray
            name="nonprofits"
            component={FormNonprofitsSelector}
            locations={locations}
            validate={validateNonprofits}
          />
        </div>
      </div>
      <div style={{ marginBottom: 30 }}>
        <FormIncludeExcludeSelector name="deedsExclude" labelText="the following deeds:" />
        <FieldArray
          name="deeds"
          component={FormEntitySelector}
          deedTypes={['Event', 'Project']}
          fetchOptions={DeedsApi.fetchDeedsForRules}
          validate={validateDeeds}
        />
      </div>
      <div style={{ marginBottom: 30 }}>
        <FormIncludeExcludeSelector name="locationsExclude" labelText="the following locations:" />
        <Field
          name="locations"
          component={FormLocationSelector}
          props={{ locations }}
          validate={validateLocations}
        />
      </div>
      <div>
        Rule summary:&#20;
        {spellOut}
      </div>
      <div style={{ marginTop: 30 }}>
        <Button type="submit" disabled={invalid} color="primary" variant="contained">
          {pristine ? 'Close' : 'Submit'}
        </Button>
      </div>
    </div>
  );
};

export default VolunteerTimeDonationCreditRule;
